/**
 * imsMgmt settings 路由（material-mgmt 下所有主檔 Pilot）。
 * 路徑 segment 須與 src/config/routes.js 的 ROUTES 常數一致。
 * 巢狀 children 供 Breadcrumb 顯示列表／新增／編輯中間層（對齊 v1）。
 *
 * hub 與各列表父路由掛 read；物料本體另掛 create／update。
 */
import MaterialSnPage from '#/modules/imsMgmt/inspectionReport/pages/MaterialSnPage';
import CreateEditInspectionKindPage from '#/modules/imsMgmt/settings/inspectionKind/pages/CreateEditInspectionKindPage';
import InspectionKindDetailPage from '#/modules/imsMgmt/settings/inspectionKind/pages/InspectionKindDetailPage';
import InspectionKindListPage from '#/modules/imsMgmt/settings/inspectionKind/pages/InspectionKindListPage';
import InspectionKindRevisionPage from '#/modules/imsMgmt/settings/inspectionKind/pages/InspectionKindRevisionPage';
import CreateEditManufacturerPage from '#/modules/imsMgmt/settings/manufacturer/pages/CreateEditManufacturerPage';
import ManufacturerListPage from '#/modules/imsMgmt/settings/manufacturer/pages/ManufacturerListPage';
import CreateEditMtrSpecPage from '#/modules/imsMgmt/settings/mtrSpec/pages/CreateEditMtrSpecPage';
import MtrSpecListPage from '#/modules/imsMgmt/settings/mtrSpec/pages/MtrSpecListPage';
import CreateEditMtrStructMainPage from '#/modules/imsMgmt/settings/mtrStruct/pages/CreateEditMtrStructMainPage';
import CreateEditMtrStructSubPage from '#/modules/imsMgmt/settings/mtrStruct/pages/CreateEditMtrStructSubPage';
import MtrStructListPage from '#/modules/imsMgmt/settings/mtrStruct/pages/MtrStructListPage';
import CreateEditMtrTemplatePage from '#/modules/imsMgmt/settings/mtrTemplate/pages/CreateEditMtrTemplatePage';
import MtrTemplateListPage from '#/modules/imsMgmt/settings/mtrTemplate/pages/MtrTemplateListPage';
import CreateEditMtrPage from '#/modules/imsMgmt/settings/mtr/pages/CreateEditMtrPage';
import MtrDetailPage from '#/modules/imsMgmt/settings/mtr/pages/MtrDetailPage';
import MtrListPage from '#/modules/imsMgmt/settings/mtr/pages/MtrListPage';
import { MATERIAL_MGMT_READ_CODES, PERM } from '#/config/permissions';
import RequirePermission from '#/modules/infra/auth/RequirePermission';
import MaterialMgmtPlaceholderPage from '#/modules/infra/pages/MaterialMgmtPlaceholderPage';

export const materialMgmtRoute = {
  path: 'material-mgmt',
  handle: { title: '物料管理' },
  children: [
    {
      index: true,
      element: (
        <RequirePermission codes={MATERIAL_MGMT_READ_CODES} mode="any">
          <MaterialMgmtPlaceholderPage />
        </RequirePermission>
      ),
    },
    {
      path: 'structure',
      handle: { title: '物料結構列表' },
      element: <RequirePermission code={PERM.MTR_READ} />,
      children: [
        {
          index: true,
          element: <MtrStructListPage />,
        },
        {
          path: 'main/create',
          handle: { title: '新增主分類' },
          element: (
            <RequirePermission code={PERM.MTR_CREATE}>
              <CreateEditMtrStructMainPage />
            </RequirePermission>
          ),
        },
        {
          path: 'main/:id/edit',
          handle: { title: '編輯主分類' },
          element: (
            <RequirePermission code={PERM.MTR_UPDATE}>
              <CreateEditMtrStructMainPage />
            </RequirePermission>
          ),
        },
        {
          path: 'sub/create',
          handle: { title: '新增子分類' },
          element: (
            <RequirePermission code={PERM.MTR_CREATE}>
              <CreateEditMtrStructSubPage />
            </RequirePermission>
          ),
        },
        {
          path: 'sub/:id/edit',
          handle: { title: '編輯子分類' },
          element: (
            <RequirePermission code={PERM.MTR_UPDATE}>
              <CreateEditMtrStructSubPage />
            </RequirePermission>
          ),
        },
      ],
    },
    {
      path: 'spec',
      handle: { title: '規格列表' },
      element: <RequirePermission code={PERM.MTR_READ} />,
      children: [
        {
          index: true,
          element: <MtrSpecListPage />,
        },
        {
          path: 'create',
          handle: { title: '新增規格' },
          element: (
            <RequirePermission code={PERM.MTR_CREATE}>
              <CreateEditMtrSpecPage />
            </RequirePermission>
          ),
        },
        {
          path: ':id/edit',
          handle: { title: '編輯規格' },
          element: (
            <RequirePermission code={PERM.MTR_UPDATE}>
              <CreateEditMtrSpecPage />
            </RequirePermission>
          ),
        },
      ],
    },
    {
      path: 'manufacturer',
      handle: { title: '製造商列表' },
      element: <RequirePermission code={PERM.MTR_READ} />,
      children: [
        {
          index: true,
          element: <ManufacturerListPage />,
        },
        {
          path: 'create',
          handle: { title: '新增製造商' },
          element: (
            <RequirePermission code={PERM.MTR_CREATE}>
              <CreateEditManufacturerPage />
            </RequirePermission>
          ),
        },
        {
          path: ':id/edit',
          handle: { title: '編輯製造商' },
          element: (
            <RequirePermission code={PERM.MTR_UPDATE}>
              <CreateEditManufacturerPage />
            </RequirePermission>
          ),
        },
      ],
    },
    {
      path: 'material-sn',
      handle: { title: '序號檢測履歷' },
      element: <RequirePermission code={PERM.INSPECTION_KIND_READ} />,
      children: [
        {
          index: true,
          element: <MaterialSnPage />,
        },
      ],
    },
    {
      path: 'inspection-kinds',
      handle: { title: '檢測種類' },
      element: <RequirePermission code={PERM.INSPECTION_KIND_READ} />,
      children: [
        {
          index: true,
          element: <InspectionKindListPage />,
        },
        {
          path: 'create',
          handle: { title: '新增檢測種類' },
          element: (
            <RequirePermission code={PERM.INSPECTION_KIND_CREATE}>
              <CreateEditInspectionKindPage />
            </RequirePermission>
          ),
        },
        {
          path: ':id/edit',
          handle: { title: '編輯檢測種類' },
          element: (
            <RequirePermission code={PERM.INSPECTION_KIND_UPDATE}>
              <CreateEditInspectionKindPage />
            </RequirePermission>
          ),
        },
        {
          path: ':id/revisions/:revisionNo',
          handle: { title: '歷史版本' },
          element: <InspectionKindRevisionPage />,
        },
        {
          path: ':id',
          handle: { title: '檢測種類詳情' },
          element: <InspectionKindDetailPage />,
        },
      ],
    },
    {
      path: 'template',
      handle: { title: '物料模板列表' },
      element: <RequirePermission code={PERM.MTR_READ} />,
      children: [
        {
          index: true,
          element: <MtrTemplateListPage />,
        },
        {
          path: 'create',
          handle: { title: '新增物料模板' },
          element: (
            <RequirePermission code={PERM.MTR_CREATE}>
              <CreateEditMtrTemplatePage />
            </RequirePermission>
          ),
        },
        {
          path: ':id/edit',
          handle: { title: '編輯物料模板' },
          element: (
            <RequirePermission code={PERM.MTR_UPDATE}>
              <CreateEditMtrTemplatePage />
            </RequirePermission>
          ),
        },
      ],
    },
    {
      path: 'material',
      handle: { title: '物料列表' },
      element: <RequirePermission code={PERM.MTR_READ} />,
      children: [
        {
          index: true,
          element: <MtrListPage />,
        },
        {
          path: 'create',
          handle: { title: '新增物料' },
          element: (
            <RequirePermission code={PERM.MTR_CREATE}>
              <CreateEditMtrPage />
            </RequirePermission>
          ),
        },
        {
          path: ':id/edit',
          handle: { title: '編輯物料' },
          element: (
            <RequirePermission code={PERM.MTR_UPDATE}>
              <CreateEditMtrPage />
            </RequirePermission>
          ),
        },
        {
          path: ':id',
          handle: { title: '物料詳情' },
          element: <MtrDetailPage />,
        },
      ],
    },
  ],
};

export const imsSettingsRoutes = [materialMgmtRoute];
